import { mdiArrowTopLeft } from '@mdi/js';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  ClientOutgoingTransfersActions,
  ClientTransfer,
} from 'common/state/banking/transfers/actions';
import { outgoingClientTransfersRecipe } from 'common/state/banking/transfers/recipes';
import {
  ClientTransfersState,
  defaultClientTransfersState,
} from 'common/state/banking/transfers/states';
import { doGetClientOutgoingTransfers } from 'common/state/banking/transfers/thunkActions';
import React, { useEffect, useMemo, useState } from 'react';

export const DepositsTable = () => {
  const [state, dispatch] = useProducerWithThunks<
    ClientTransfersState,
    ClientOutgoingTransfersActions
  >(outgoingClientTransfersRecipe, defaultClientTransfersState);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [destinations, setDestinations] = useState<{
    [url: string]: Record<string, string>;
  }>({});

  const columns = useMemo(
    (): ColumnProp<ClientTransfer>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.date).toLocaleString(),
      },
      {
        key: 'sender',
        title: 'Sender',
        valueFormatter: (row) => row.sender.split('/').slice(-2, -1)[0],
      },
      { key: 'description', title: 'Description' },
      {
        key: 'destination',
        title: 'Destination',
        valueFormatter: (row) =>
          destinations[row.destination]?.title || 'Loading...',
      },
      { key: 'reference', title: 'Reference' },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement({ colour: false }),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
      {
        key: 'got_amount',
        title: 'Got Amount',
        valueFormatter: (row) =>
          new Currency(row.got_amount, row.got_currency).toElement({
            colour: false,
          }),
        csvFormatter: (row) =>
          new Currency(row.got_amount, row.got_currency).toString({
            symbol: false,
          }),
      },
      { key: 'status', title: 'Status' },
    ],
    [destinations]
  );

  useEffect(() => {
    dispatch(
      doGetClientOutgoingTransfers({
        limit: pageSize,
        offset: pageSize * (pageNo - 1),
      })
    );
  }, [dispatch, pageNo, pageSize]);

  useEffect(() => {
    if (state.transfers) {
      const urls = state.transfers.reduce((acc, transfer) => {
        if (!(transfer.destination in destinations)) {
          acc.add(transfer.destination);
        }
        return acc;
      }, new Set<string>());

      if (urls?.size) {
        Promise.all(
          Array.from(urls).map((url) =>
            api.get(url).then((response) => response.data)
          )
        ).then((newDestinations) => {
          setDestinations({
            ...destinations,
            ...newDestinations.reduce(
              (acc, destination) => ({
                ...acc,
                [destination.url]: destination,
              }),
              {}
            ),
          });
        });
      }
    }
    //eslint-disable-next-line
  }, [state.transfers]);

  return (
    <>
      <TableTitle icon={mdiArrowTopLeft} title="Deposits" />
      <BaseTable
        columns={columns}
        rows={state.transfers || []}
        csvName="Deposits"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
